import type { HTMLAttributes } from "react";

type SkeletonBlockProps = HTMLAttributes<HTMLDivElement>;

const pulseClasses =
  "relative overflow-hidden rounded-xl bg-slate-200/80 dark:bg-slate-700/70 motion-safe:animate-pulse motion-reduce:animate-none motion-reduce:opacity-80";

function cn(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(" ");
}

export function SkeletonBlock({ className, ...rest }: SkeletonBlockProps) {
  return <div className={cn(pulseClasses, className)} aria-hidden="true" {...rest} />;
}

interface SkeletonCardProps {
  lines?: number;
  className?: string;
  showAvatar?: boolean;
}

export function SkeletonCard({
  lines = 3,
  className,
  showAvatar = false,
}: SkeletonCardProps) {
  return (
    <div
      className={cn(
        "w-full rounded-3xl border border-slate-200 bg-white/80 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/70",
        className,
      )}
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-center gap-4">
        {showAvatar ? <SkeletonAvatar size={48} /> : null}
        <SkeletonBlock className="h-5 w-1/3" />
      </div>
      <div className="mt-4 flex flex-col gap-3">
        {Array.from({ length: lines }).map((_, index) => (
          <SkeletonBlock
            key={`line-${index + 1}`}
            className={cn(
              "h-4",
              index === lines - 1 ? "w-2/5" : "w-full",
            )}
          />
        ))}
      </div>
    </div>
  );
}

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export function SkeletonTable({
  rows = 4,
  columns = 3,
  className,
}: SkeletonTableProps) {
  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/70",
        className,
      )}
      role="presentation"
      aria-hidden="true"
    >
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {Array.from({ length: rows }).map((_, row) => (
          <div key={`row-${row}`} className="flex gap-4 px-5 py-4">
            {Array.from({ length: columns }).map((__, col) => (
              <SkeletonBlock
                key={`col-${col}`}
                className={cn(
                  "h-4 flex-1",
                  col === 0 ? "w-32 flex-none" : "",
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

interface SkeletonListProps {
  count?: number;
  className?: string;
}

export function SkeletonList({ count = 3, className }: SkeletonListProps) {
  return (
    <div className={cn("flex flex-col gap-4", className)} aria-hidden="true">
      {Array.from({ length: count }).map((_, idx) => (
        <SkeletonCard key={`skeleton-${idx + 1}`} />
      ))}
    </div>
  );
}

interface SkeletonAvatarProps {
  size?: number;
  className?: string;
}

export function SkeletonAvatar({ size = 40, className }: SkeletonAvatarProps) {
  return (
    <div
      className={cn(
        pulseClasses,
        "rounded-2xl",
        className,
      )}
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
}
